import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuth } from '@/components/auth/AuthContext'
import { useAccount } from '@/modules/accounts/hooks'
import { useCommandPalette } from '@/components/command/useCommandPalette'
import { useTutorialProgress } from '@/components/tutorials/useTutorialProgress'
import { useFirstRun } from './FirstRunProvider'

/** The explanation video the "Review a decision" step plays inside Help. */
export const SETUP_TUTORIAL_ID = 'needs-your-attention'

export type SetupStepId = 'business-setup' | 'sample' | 'tour' | 'paid'

export interface SetupStep {
  id: SetupStepId
  title: string
  sub: string
  done: boolean
  /** The business setup is unfinished — the number is not ready yet. */
  urgent: boolean
  /**
   * `to` navigates; `run` performs the step. `inPlace` marks a `run` that
   * leaves the person where they are (Retry, or Watch — which opens the video
   * inside Help), so the Help drawer doesn't close before running it.
   */
  action?: { label: string; to?: string; run?: () => void; inPlace?: boolean }
}

export interface SetupSteps {
  /** Owner-only, and only while at least one step is open. */
  visible: boolean
  isOwner: boolean
  steps: SetupStep[]
  done: number
  total: number
  /** The first open step — the one the Home banner offers. */
  next?: SetupStep
  setupRequired: boolean
}

/**
 * The four "Get set up" outcomes, shared by the Home setup banner and the Help
 * drawer's Get set up section so both always agree. Setup completion comes from
 * the saved business conversation; the sample comes from first-run state;
 * "Review a decision" is the 30-second Needs your attention video (done once
 * it has been watched, or the Home coach tour completed); "Get paid" is Stripe
 * Connect onboarding.
 */
export function useSetupSteps(): SetupSteps {
  const { accountId, user } = useAuth()
  const { data: profile } = useAccount(accountId ?? '')
  const setup = useQuery({
    queryKey: ['business-setup', accountId],
    queryFn: () => api<{ required: boolean }>('/business-setup'),
    enabled: !!accountId,
  })
  const firstRun = useFirstRun()
  const { requestHelp } = useCommandPalette()
  // Any watched version counts: the step is about having seen it once, and a
  // film's version bump only re-surfaces its peek tab.
  const { progress: tutorialProgress } = useTutorialProgress()
  const watchedDecisionVideo = tutorialProgress[SETUP_TUTORIAL_ID]?.seenVersion !== undefined
  const isOwner = user?.accountRole !== 'MEMBER' && user?.role !== 'MEMBER'

  const setupRequired = setup.data?.required === true
  const steps: SetupStep[] = [
    {
      id: 'business-setup',
      title: 'Complete Ten Four setup',
      sub: setup.isError
        ? 'Your setup status couldn’t load. Try again.'
        : !setup.data
          ? 'Checking your setup…'
          : setupRequired
            ? 'Pick up where you left off. A few details about your work will get your number ready.'
            : 'Your business details are saved. You can keep adding to them in Brain.',
      done: setup.data?.required === false,
      urgent: setupRequired,
      action: setup.isError
        ? { label: 'Retry', run: () => { void setup.refetch() }, inPlace: true }
        : setupRequired
          ? { label: 'Continue setup', to: '/setup/business' }
          : undefined,
    },
    {
      id: 'sample',
      title: 'See how tenfour answers',
      sub: 'A short sample call, handled on the Calls page.',
      done: firstRun.state.sample,
      urgent: false,
      action: { label: 'Play it', run: firstRun.playSample },
    },
    {
      id: 'tour',
      title: 'Review a decision',
      sub: 'A 30-second video of Needs your attention.',
      done: firstRun.state.tour === 'completed' || watchedDecisionVideo,
      urgent: false,
      action: {
        label: 'Watch',
        run: () => requestHelp({ view: 'tutorial', tutorialId: SETUP_TUTORIAL_ID }),
        inPlace: true,
      },
    },
    {
      id: 'paid',
      title: 'Get paid',
      sub: 'Connect Stripe so invoices can be paid online.',
      done: Boolean(profile?.stripeAccountId && profile?.stripeOnboardingComplete),
      urgent: false,
      action: { label: 'Connect', to: '/money?tab=payouts' },
    },
  ]

  const done = steps.filter((step) => step.done).length
  return {
    visible: isOwner && done < steps.length,
    isOwner,
    steps,
    done,
    total: steps.length,
    next: steps.find((step) => !step.done),
    setupRequired,
  }
}
